import { Anchor, Content, Portal, Root, Trigger } from '@radix-ui/react-popover';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { POPPER_SCROLLER_CLASSES } from '../scroll/scroll.tokens';

export const Popover = Root;
export const PopoverTrigger = Trigger;
export const PopoverAnchor = Anchor;

export type PopoverContentProps = ComponentPropsWithoutRef<typeof Content>;

/**
 * `align="start"` is LOGICAL in Radix — it aligns to the inline start, which is
 * the right edge in Arabic. That is why the default here is `start` rather than
 * `center`: a column menu should hang from the same edge as its trigger, in
 * whichever direction the document runs.
 *
 * **The ceiling is not optional.** A popover had no `max-height` at all until
 * P10.1, so a picker opened near the bottom of a page — or any popover at all on
 * a 320px phone — grew straight past the viewport edge, taking its own actions
 * with it. Radix measures the space between the trigger and that edge on every
 * open and publishes it as `--radix-popover-content-available-height`; the
 * preset names it `max-h-popover-available`, and the panel scrolls inside
 * whatever it turns out to be.
 *
 * A panel that brings its OWN scroller (the `MultiSelect` option list, the
 * `ServerSelect` command list) overrides the outer `overflow-y-auto` with
 * `overflow-hidden` and lays itself out as a flex column, so the clamp is
 * enforced once and there are never two nested scrollbars. `cn`'s
 * tailwind-merge makes that a one-class override at the call site.
 */
export const PopoverContent = forwardRef<ElementRef<typeof Content>, PopoverContentProps>(
  function PopoverContent({ className, align = 'start', sideOffset = 6, ...props }, ref) {
    return (
      <Portal>
        <Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            'z-popper rounded-md border border-border bg-popover p-lg text-popover-foreground shadow-lg outline-none',
            'max-h-popover-available max-w-popover-available',
            POPPER_SCROLLER_CLASSES,
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
          )}
          {...props}
        />
      </Portal>
    );
  },
);
